import React, { Component } from 'react';
import { NavLink } from 'react-router-dom';
import { withStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import Snackbar from '@material-ui/core/Snackbar';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';

import GlobalNav from './components/GlobalNav';
import ExploreCard from './ExploreCard';
import MetaTags from './components/MetaTags';
import { CATEGORIES, getAllPalettes } from './helpers/exploreData';
import styles from './styles/ExploreStyles';

const PAGE_SIZE = 24;
const LIKES_KEY = 'explore_likes';

function loadLikes() {
  try {
    const raw = window.localStorage.getItem(LIKES_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return new Set(parsed);
    }
  } catch (e) {
    /* ignore */
  }
  return new Set();
}

class Explore extends Component {
  constructor(props) {
    super(props);
    this.state = {
      sort: 'popular',
      search: '',
      visible: PAGE_SIZE,
      liked: loadLikes(),
      shuffleSeed: 1,
      snack: '',
    };
    this.onCopy = this.onCopy.bind(this);
    this.onToggleLike = this.onToggleLike.bind(this);
  }

  get category() {
    return this.props.match.params.category || null;
  }

  componentDidUpdate(prevProps) {
    // Reset pagination when the category route changes.
    if (prevProps.match.params.category !== this.props.match.params.category) {
      this.setState({ visible: PAGE_SIZE });
    }
  }

  onCopy(hex) {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(hex).catch(() => {});
    }
    this.setState({ snack: `${hex.toUpperCase()} copied to clipboard` });
  }

  onToggleLike(id) {
    this.setState(st => {
      const liked = new Set(st.liked);
      if (liked.has(id)) liked.delete(id);
      else liked.add(id);
      try {
        window.localStorage.setItem(LIKES_KEY, JSON.stringify([...liked]));
      } catch (e) {
        /* ignore */
      }
      return { liked };
    });
  }

  setSort(sort) {
    this.setState(st => ({
      sort,
      visible: PAGE_SIZE,
      shuffleSeed: sort === 'random' ? st.shuffleSeed + 1 : st.shuffleSeed,
    }));
  }

  buildList() {
    const { sort, search, shuffleSeed } = this.state;
    let list = getAllPalettes();

    if (this.category) list = list.filter(p => p.category === this.category);

    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter(
        p =>
          p.category.includes(q) ||
          p.colors.some(c => c.toLowerCase().includes(q.replace('#', '')))
      );
    }

    if (sort === 'popular') {
      list = [...list].sort((a, b) => b.likes - a.likes);
    } else if (sort === 'new') {
      list = [...list].sort((a, b) => b.createdAt - a.createdAt);
    } else if (sort === 'random') {
      // Deterministic shuffle for the current seed so it doesn't churn on render.
      list = [...list]
        .map(p => ({ p, k: ((p.createdAt + 1) * 2654435761 * shuffleSeed) % 1000003 }))
        .sort((a, b) => a.k - b.k)
        .map(x => x.p);
    }
    return list;
  }

  render() {
    const { classes } = this.props;
    const { sort, search, visible, liked, snack } = this.state;
    const category = this.category;
    const activeCat = CATEGORIES.find(c => c.id === category);
    const list = this.buildList();
    const shown = list.slice(0, visible);

    const modeButton = (id, label) => (
      <Button
        className={`${classes.modeButton} ${sort === id ? classes.modeActive : ''}`}
        onClick={() => this.setSort(id)}
      >
        {label}
      </Button>
    );

    return (
      <div className={classes.root}>
        <MetaTags
          title="Explore Color Palettes - Custom Color"
          description="Browse hundreds of curated color palettes by category — pastel, vintage, retro, neon, dark, warm and more. Copy colors or open any palette in the generator."
          keywords="color palettes, explore palettes, pastel, vintage, retro, neon, palette categories"
          url={window.location.href}
        />
        <GlobalNav />

        <div className={classes.layout}>
          <aside className={classes.sidebar}>
            <div className={classes.modeRow}>
              {modeButton('popular', 'Popular')}
              {modeButton('new', 'New')}
              {modeButton('random', 'Random')}
            </div>

            <div className={classes.catHeader}>Categories</div>
            <nav className={classes.catList}>
              <NavLink exact to="/explore" className={classes.catLink} activeClassName={classes.catLinkActive}>
                All
              </NavLink>
              {CATEGORIES.map(c => (
                <NavLink
                  key={c.id}
                  to={`/explore/${c.id}`}
                  className={classes.catLink}
                  activeClassName={classes.catLinkActive}
                >
                  {c.label}
                </NavLink>
              ))}
            </nav>
          </aside>

          <main className={classes.main}>
            <h1 className={classes.heading}>
              {activeCat ? `${activeCat.label} palettes` : 'Explore palettes'}
            </h1>
            <p className={classes.subheading}>{list.length} palettes</p>

            <TextField
              className={classes.search}
              fullWidth
              variant="outlined"
              size="small"
              placeholder="Search by category or hex…"
              value={search}
              onChange={e => this.setState({ search: e.target.value, visible: PAGE_SIZE })}
              inputProps={{ 'aria-label': 'Search palettes' }}
            />

            {shown.length === 0 ? (
              <div className={classes.empty}>No palettes match your search.</div>
            ) : (
              <div className={classes.grid}>
                {shown.map(p => (
                  <ExploreCard
                    key={p.id}
                    palette={p}
                    liked={liked.has(p.id)}
                    onToggleLike={this.onToggleLike}
                    onCopy={this.onCopy}
                  />
                ))}
              </div>
            )}

            {visible < list.length && (
              <div className={classes.loadMoreRow}>
                <Button
                  variant="outlined"
                  onClick={() => this.setState(st => ({ visible: st.visible + PAGE_SIZE }))}
                >
                  Load more
                </Button>
              </div>
            )}
          </main>
        </div>

        <Snackbar
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          open={Boolean(snack)}
          autoHideDuration={2200}
          onClose={() => this.setState({ snack: '' })}
          message={snack}
          action={
            <IconButton size="small" color="inherit" onClick={() => this.setState({ snack: '' })}>
              <CloseIcon fontSize="small" />
            </IconButton>
          }
        />
      </div>
    );
  }
}

export default withStyles(styles)(Explore);
